import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import { startCheckout, getPricing } from "../lib/payments";
import { useTick } from "../hooks/useTick";
import { toast } from "sonner";
import { Check, CreditCard } from "lucide-react";

const SERVICES = ["Medical Courier", "DNA Testing", "Drug Testing", "Notary Services", "Phlebotomy (Waitlist)"];
const EMPTY = { name: "", email: "", phone: "", service: "Medical Courier", pickup_location: "", dropoff_location: "", preferred_date: "", details: "" };

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-background/50">{label}</span>
    <input
      {...props}
      className="w-full bg-transparent border-b border-background/25 focus:border-accent py-3 mt-1 font-mono text-sm text-background outline-none transition-colors duration-300 placeholder:text-background/30"
    />
  </label>
);

export const Booking = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [lastReq, setLastReq] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [payLoading, setPayLoading] = useState("");
  const tick = useTick();

  useEffect(() => {
    getPricing().then(setPricing).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    tick(520);
    setLoading(true);
    try {
      const { data } = await api.post("/service-requests", form);
      setLastReq({ id: data.id, service: form.service, email: form.email });
      setDone(true);
      setForm(EMPTY);
      toast.success("Request received. Our dispatch team will reach out shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const pay = async (type) => {
    tick(520);
    setPayLoading(type);
    try {
      await startCheckout({
        payment_type: type,
        service: lastReq?.service,
        request_id: lastReq?.id,
        customer_email: lastReq?.email,
      });
    } catch {
      toast.error("Unable to start checkout. Please try again.");
      setPayLoading("");
    }
  };

  const servicePrice = lastReq && pricing?.services?.[lastReq.service];

  return (
    <section id="booking" className="bg-foreground text-background py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-background/50">/ Request Service</span>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase mt-5 leading-[0.95]">
            Book a<br /><span className="text-accent">Pickup</span>
          </h2>
          <p className="text-sm md:text-base font-mono text-background/60 leading-relaxed mt-8 max-w-sm">
            Tell us what you need moved or tested. We confirm every request personally, 24/7.
          </p>
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <motion.div
              data-testid="booking-success"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="border border-background/20 p-12 flex flex-col items-start gap-6"
            >
              <div className="w-14 h-14 bg-accent flex items-center justify-center">
                <Check size={28} className="text-accent-foreground" />
              </div>
              <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">Request Received.</h3>
              <p className="font-mono text-sm text-background/60 max-w-md">
                Thank you. A BioFleet dispatch coordinator will contact you shortly to confirm details and timing.
              </p>

              <div className="w-full border-t border-background/20 pt-6 mt-2">
                <div className="flex items-center gap-2 mb-4 text-background/50">
                  <CreditCard size={15} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em]">Optional — Pay Now to Confirm Faster</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="booking-pay-deposit"
                    onClick={() => pay("deposit")}
                    disabled={!!payLoading}
                    className="bg-accent text-accent-foreground font-mono uppercase tracking-[0.12em] text-[11px] font-bold py-4 px-8 border border-accent hover:bg-transparent hover:text-accent transition-colors duration-300 disabled:opacity-50"
                  >
                    {payLoading === "deposit" ? "Redirecting..." : `Pay $${pricing?.deposit?.toFixed(2) ?? "25.00"} Deposit`}
                  </button>
                  {servicePrice && (
                    <button
                      data-testid="booking-pay-service"
                      onClick={() => pay("service")}
                      disabled={!!payLoading}
                      className="font-mono uppercase tracking-[0.12em] text-[11px] font-bold py-4 px-8 border border-background/30 hover:border-accent hover:text-accent transition-colors duration-300 disabled:opacity-50"
                    >
                      {payLoading === "service" ? "Redirecting..." : `Pay Full — $${servicePrice.toFixed(2)}`}
                    </button>
                  )}
                </div>
              </div>

              <button
                data-testid="booking-new"
                onClick={() => setDone(false)}
                className="font-mono uppercase tracking-[0.15em] text-xs text-background/50 hover:text-background transition-colors duration-300 underline underline-offset-4"
              >
                Submit Another Request
              </button>
            </motion.div>
          ) : (
            <form data-testid="booking-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
              <Field data-testid="booking-name" label="Full Name *" required value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              <Field data-testid="booking-email" label="Email *" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" />
              <Field data-testid="booking-phone" label="Phone *" required value={form.phone} onChange={set("phone")} placeholder="(678) 000-0000" />
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-background/50">Service *</span>
                <select
                  data-testid="booking-service"
                  value={form.service}
                  onChange={set("service")}
                  className="w-full bg-transparent border-b border-background/25 focus:border-accent py-3 mt-1 font-mono text-sm text-background outline-none transition-colors duration-300"
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s} className="bg-foreground text-background">{s}</option>
                  ))}
                </select>
              </label>
              <Field data-testid="booking-pickup" label="Pickup Location" value={form.pickup_location} onChange={set("pickup_location")} placeholder="Address / facility" />
              <Field data-testid="booking-dropoff" label="Dropoff Location" value={form.dropoff_location} onChange={set("dropoff_location")} placeholder="Address / lab" />
              <Field data-testid="booking-date" label="Preferred Date" type="date" value={form.preferred_date} onChange={set("preferred_date")} />
              <div className="md:col-span-2">
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-background/50">Details</span>
                  <textarea
                    data-testid="booking-details"
                    rows={3}
                    value={form.details}
                    onChange={set("details")}
                    placeholder="Anything we should know..."
                    className="w-full bg-transparent border-b border-background/25 focus:border-accent py-3 mt-1 font-mono text-sm text-background outline-none transition-colors duration-300 resize-none placeholder:text-background/30"
                  />
                </label>
              </div>
              <div className="md:col-span-2">
                <button
                  data-testid="booking-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-accent text-accent-foreground font-mono uppercase tracking-[0.15em] text-xs font-bold py-5 px-12 border border-accent hover:bg-transparent hover:text-accent transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
